import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { ModalState, PresentationForm, Presentation } from '../types/system-parameters.types';

interface PresentationModalProps {
  modalState: ModalState;
  onClose: () => void;
  onSave: (formData: PresentationForm, isEdit?: boolean, itemId?: string) => Promise<void>;
}

export const PresentationModal: React.FC<PresentationModalProps> = ({
  modalState,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<PresentationForm>({
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  // Resetear formulario cuando se abra en modo crear o cuando cambie el item
  useEffect(() => {
    if (modalState.isOpen) {
      if (modalState.mode === 'edit' && modalState.item) {
        const presentation = modalState.item as Presentation;
        setFormData({
          name: presentation.additionalData.name,
          description: presentation.additionalData.description
        });
      } else {
        setFormData({
          name: '',
          description: ''
        });
      }
    }
  }, [modalState.isOpen, modalState.mode, modalState.item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(
        formData,
        modalState.mode === 'edit',
        modalState.item?.id
      );
      onClose();
    } catch (error) {
      console.error('Error al guardar presentación:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={modalState.isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {modalState.mode === 'edit' ? 'Editar Presentación' : 'Nueva Presentación'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Ej: Simple"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descripción de la presentación"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!formData.name.trim() || loading}
              className="bg-macartur-primary"
            >
              {loading ? 'Guardando...' : modalState.mode === 'edit' ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};